/* BlueprintReferencesPanel — auto-generated bibliography for an entire blueprint.
 *
 * Fetches GET /api/countries/{id}/references and renders a numbered list of every
 * distinct (Source, YYYY) citation across all 31 sections, colour-coded by
 * classification (API · Source · Canon · Unverified) with per-section footprint.
 *
 * Renders as a collapsible section at the bottom of the blueprint view, plus a
 * "Download References Page" button that exports a print-ready HTML file for
 * lifting into any PDF export.
 *
 * When an Unverified entry is selected with the filter, an "Auto-replace" button
 * appears that triggers POST /api/countries/{id}/rewrite-citation to have Claude
 * propose a replacement sentence citing only allowlisted sources.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BookOpen, Download, Loader2, ShieldCheck, Wand2, CheckCircle2,
  AlertTriangle, ChevronDown, ChevronRight, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLS_STYLE = {
  api:        { label: 'LIVE API',     tone: 'bg-cyan-500/15 text-cyan-700 border-cyan-500/30' },
  source:     { label: 'CURATED',      tone: 'bg-violet-500/15 text-violet-700 border-violet-500/30' },
  canon:      { label: 'CANONICAL',    tone: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  unverified: { label: 'UNVERIFIED',   tone: 'bg-rose-500/15 text-rose-700 border-rose-500/30' },
};

export default function BlueprintReferencesPanel({ countryId, countryName, darkMode = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  // State for the rewrite modal / inline card
  const [rewriting, setRewriting] = useState(null);   // entry being rewritten
  const [rewriteResult, setRewriteResult] = useState(null);
  const [rewriteErr, setRewriteErr] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = async () => {
    if (data || loading) return;
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/countries/${countryId}/references`, { timeout: 40000 });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load references');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Refetch whenever a rewrite is applied so the classification counts update
  const reload = async () => {
    setData(null);
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/countries/${countryId}/references`, { timeout: 40000 });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not reload references');
    } finally {
      setLoading(false);
    }
  };

  const startRewrite = async (entry) => {
    setRewriting(entry);
    setRewriteResult(null);
    setRewriteErr(null);
    // Default: use the first section the citation appears in
    const primarySection = entry.sections[0];
    try {
      const r = await axios.post(
        `${API}/countries/${countryId}/rewrite-citation`,
        { section_name: primarySection, unverified_citation: entry.raw },
        { timeout: 60000 },
      );
      setRewriteResult({ ...r.data, section_name: primarySection });
    } catch (e) {
      setRewriteErr(e.response?.data?.detail || 'Rewrite request failed');
    }
  };

  const applyRewrite = async () => {
    if (!rewriteResult) return;
    setApplying(true);
    try {
      await axios.post(
        `${API}/countries/${countryId}/apply-citation-rewrite`,
        {
          section_name: rewriteResult.section_name,
          original_sentence: rewriteResult.original_sentence,
          rewritten_sentence: rewriteResult.rewritten_sentence,
        },
        { timeout: 20000 },
      );
      setRewriting(null);
      setRewriteResult(null);
      await reload();
    } catch (e) {
      setRewriteErr(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const download = () => {
    if (!data) return;
    const html = buildPrintableHTML(data, countryName);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(countryName || 'blueprint').replace(/\s+/g, '_')}_References.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const entries = data?.entries || [];
  const filtered = filter === 'all' ? entries : entries.filter(e => e.classification === filter);

  return (
    <div
      data-testid="blueprint-references-panel"
      className={`rounded-xl border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'} shadow-sm my-8`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="toggle-references-panel"
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7c3aed] to-[#ec4899] flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${darkMode ? 'text-white/50' : 'text-slate-500'}`}>
              Sources & References
            </p>
            <p className={`text-base font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              {data ? `${data.totals.unique_sources} unique sources` : 'Auto-generated bibliography'}
              {data && (
                <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-white/50' : 'text-slate-500'}`}>
                  · {data.totals.total_citations_across_sections} citations across {data.totals.sections_analysed} sections
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data && (
            <>
              <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-700 border border-cyan-500/30">
                {data.totals.api} API
              </span>
              <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-700 border border-violet-500/30">
                {data.totals.source} SRC
              </span>
              <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 border border-emerald-500/30">
                {data.totals.canon} CANON
              </span>
              {data.totals.unverified > 0 && (
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-700 border border-rose-500/30">
                  {data.totals.unverified} flagged
                </span>
              )}
            </>
          )}
          {open ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
        </div>
      </button>

      {open && (
        <div className={`px-5 pb-5 border-t ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Scanning all 31 sections for citations…
            </div>
          )}
          {err && (
            <div className="flex items-center gap-2 text-sm text-rose-600 py-6">
              <AlertTriangle className="w-4 h-4" /> {err}
            </div>
          )}
          {data && (
            <>
              {/* Filter chips */}
              <div className="flex items-center gap-2 flex-wrap pt-4 pb-3" data-testid="references-filters">
                {[
                  { k: 'all',        label: 'All',        n: data.totals.unique_sources, tone: 'bg-slate-200 text-slate-700' },
                  { k: 'api',        label: 'Live API',   n: data.totals.api,           tone: CLS_STYLE.api.tone },
                  { k: 'source',     label: 'Curated',    n: data.totals.source,        tone: CLS_STYLE.source.tone },
                  { k: 'canon',      label: 'Canonical',  n: data.totals.canon,         tone: CLS_STYLE.canon.tone },
                  { k: 'unverified', label: 'Unverified', n: data.totals.unverified,    tone: CLS_STYLE.unverified.tone },
                ].map((c) => (
                  <button
                    key={c.k}
                    type="button"
                    data-testid={`refs-filter-${c.k}`}
                    onClick={() => setFilter(c.k)}
                    className={`px-3 py-1 rounded-full text-[11px] font-bold border transition-all ${
                      filter === c.k
                        ? 'ring-2 ring-offset-1 ring-slate-400 ' + c.tone
                        : c.tone + ' opacity-60 hover:opacity-100'
                    }`}
                  >
                    {c.label} · {c.n}
                  </button>
                ))}
                <div className="ml-auto">
                  <button
                    type="button"
                    onClick={download}
                    data-testid="references-download"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-[#0b1c30] text-white hover:bg-[#1a365d] transition-all"
                  >
                    <Download className="w-3 h-3" /> Download references page
                  </button>
                </div>
              </div>

              {/* List */}
              <ol className={`space-y-1.5 mt-2 text-[12.5px] ${darkMode ? 'text-white/80' : 'text-slate-700'}`} data-testid="references-list">
                {filtered.map((e) => {
                  const st = CLS_STYLE[e.classification] || CLS_STYLE.unverified;
                  const isUnverified = e.classification === 'unverified';
                  return (
                    <li
                      key={e.n}
                      data-testid={`ref-entry-${e.n}`}
                      className={`flex items-start gap-3 p-2 rounded ${darkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                    >
                      <span className={`shrink-0 text-[10px] font-black w-8 text-right ${darkMode ? 'text-white/40' : 'text-slate-400'}`}>
                        [{e.n}]
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{e.raw}</span>
                          <span className={`text-[9.5px] font-black px-1.5 py-0.5 rounded-full border ${st.tone}`}>
                            {st.label}
                          </span>
                          <span className={`text-[10px] ${darkMode ? 'text-white/40' : 'text-slate-400'}`}>
                            × {e.count}
                          </span>
                          {isUnverified && (
                            <button
                              type="button"
                              onClick={() => startRewrite(e)}
                              data-testid={`rewrite-btn-${e.n}`}
                              className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold bg-gradient-to-r from-[#7c3aed] to-[#ec4899] text-white hover:opacity-90 transition-opacity"
                            >
                              <Wand2 className="w-2.5 h-2.5" /> Auto-replace
                            </button>
                          )}
                        </div>
                        <p className={`text-[10.5px] mt-0.5 ${darkMode ? 'text-white/50' : 'text-slate-500'}`}>
                          Used in {e.sections.length} section{e.sections.length > 1 ? 's' : ''}: {e.sections.slice(0, 5).join(', ')}
                          {e.sections.length > 5 ? ` … +${e.sections.length - 5} more` : ''}
                        </p>
                      </div>
                    </li>
                  );
                })}
                {filtered.length === 0 && (
                  <li className={`text-sm italic ${darkMode ? 'text-white/40' : 'text-slate-400'}`}>No entries in this bucket.</li>
                )}
              </ol>

              {/* Legend & caveat */}
              <div className={`mt-4 p-3 rounded-lg text-[11px] ${darkMode ? 'bg-white/5 text-white/60' : 'bg-slate-50 text-slate-500'}`}>
                <p className="mb-1 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> <strong>Classification key.</strong></p>
                <p><strong>Live API</strong> — citation matches a live-polled provider (World Bank, IMF, UNCTAD, WHO, NASA POWER, UN Comtrade, TI CPI, V-Dem, Freedom House, REST Countries, WGI, Findex, ECB FX, GDELT, Wikipedia, OECD).</p>
                <p><strong>Curated</strong> — matches the institutional source pool injected into every prompt.</p>
                <p><strong>Canonical</strong> — matches a universally-accepted institutional name (multilateral, rating agency, major media, national central bank).</p>
                <p><strong>Unverified</strong> — citation format is valid but the source name is not in any allowlist. Flagged for manual review before publication. Use the <strong>Auto-replace</strong> button to have Claude suggest an allowlist-only rewrite.</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Rewrite suggestion modal */}
      {rewriting && (
        <div
          data-testid="rewrite-modal"
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(ev) => { if (ev.target === ev.currentTarget) { setRewriting(null); setRewriteResult(null); setRewriteErr(null); } }}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-fuchsia-600 mb-1 flex items-center gap-1.5">
                  <Wand2 className="w-3 h-3" /> Citation auto-replace
                </p>
                <h3 className="text-lg font-black text-slate-900">{rewriting.raw}</h3>
                <p className="text-[11px] text-slate-500 mt-1">Flagged as <strong className="text-rose-600">UNVERIFIED</strong> · used {rewriting.count}× in {rewriting.sections.length} section{rewriting.sections.length > 1 ? 's' : ''}</p>
              </div>
              <button
                type="button"
                onClick={() => { setRewriting(null); setRewriteResult(null); setRewriteErr(null); }}
                className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {!rewriteResult && !rewriteErr && (
                <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-fuchsia-500" />
                  Asking Claude for an allowlist-only rewrite…
                </div>
              )}
              {rewriteErr && (
                <div className="p-3 rounded-lg bg-rose-50 text-rose-700 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {rewriteErr}
                </div>
              )}
              {rewriteResult && (
                <>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">Original sentence · {rewriteResult.section_name.replace(/_/g, ' ')}</p>
                    <div className="p-3 rounded-lg bg-rose-50 text-[12.5px] text-slate-800 leading-relaxed line-through decoration-rose-400/60 decoration-1">
                      {rewriteResult.original_sentence}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600 mb-1.5 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3" /> Proposed rewrite · confidence {rewriteResult.confidence}
                    </p>
                    <div className="p-3 rounded-lg bg-emerald-50 text-[12.5px] text-slate-900 leading-relaxed font-medium" data-testid="rewrite-preview">
                      {rewriteResult.rewritten_sentence}
                    </div>
                  </div>
                  {Array.isArray(rewriteResult.new_citations) && rewriteResult.new_citations.length > 0 && (
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">New citations introduced</p>
                      <div className="flex flex-wrap gap-1.5">
                        {rewriteResult.new_citations.map((c, i) => (
                          <span key={i} className="text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 border border-cyan-200">
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {rewriteResult.changed_facts && (
                    <p className="text-[11px] text-slate-500 italic">What changed: {rewriteResult.changed_facts}</p>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button
                type="button"
                onClick={() => { setRewriting(null); setRewriteResult(null); setRewriteErr(null); }}
                className="px-3 py-1.5 rounded-lg text-[12px] font-bold text-slate-600 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!rewriteResult || applying}
                onClick={applyRewrite}
                data-testid="apply-rewrite"
                className="px-3 py-1.5 rounded-lg text-[12px] font-bold text-white bg-gradient-to-r from-[#7c3aed] to-[#ec4899] hover:opacity-90 disabled:opacity-40 transition-opacity"
              >
                {applying ? 'Applying…' : 'Apply to blueprint'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Printable HTML export (lift into PDF export as-is) ──────────────────────
function buildPrintableHTML(data, countryName) {
  const CLS_COLOR = { api: '#0891b2', source: '#7c3aed', canon: '#10b981', unverified: '#e11d48' };
  const rows = data.entries.map(e => `
    <tr>
      <td class="n">[${e.n}]</td>
      <td class="src"><strong>${escapeHTML(e.raw)}</strong></td>
      <td class="cls" style="color:${CLS_COLOR[e.classification] || '#64748b'}">${(CLS_STYLE[e.classification] || CLS_STYLE.unverified).label}</td>
      <td class="cnt">× ${e.count}</td>
      <td class="secs">${escapeHTML(e.sections.join(', '))}</td>
    </tr>
  `).join('');
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHTML(countryName)} — Sources & References</title>
<style>
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#0b1c30;margin:2em;line-height:1.4}
h1{font-size:26px;margin:0 0 4px}
.subtitle{color:#64748b;margin-bottom:20px;font-size:12px}
.totals{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin:20px 0;padding:12px;background:#f1f5f9;border-radius:8px;font-size:12px}
.totals .k{font-weight:900;font-size:10px;letter-spacing:.12em;color:#64748b;text-transform:uppercase}
.totals .v{font-size:20px;font-weight:900}
table{border-collapse:collapse;width:100%;font-size:11.5px}
td{border-bottom:1px solid #e2e8f0;padding:6px 8px;vertical-align:top}
td.n{width:40px;color:#94a3b8;font-variant-numeric:tabular-nums}
td.src{width:38%}
td.cls{width:90px;font-size:9.5px;font-weight:900;letter-spacing:.08em;text-transform:uppercase}
td.cnt{width:50px;color:#64748b;text-align:right;font-variant-numeric:tabular-nums}
td.secs{color:#64748b;font-size:10.5px}
footer{margin-top:24px;font-size:10px;color:#94a3b8}
</style></head>
<body>
<h1>${escapeHTML(countryName)} — Sources & References</h1>
<p class="subtitle">Auto-generated bibliography · ${data.totals.unique_sources} unique sources · ${data.totals.total_citations_across_sections} inline citations across ${data.totals.sections_analysed} sections · Generated ${new Date(data.generated_at).toLocaleString()}</p>
<div class="totals">
  <div><div class="k">Unique</div><div class="v">${data.totals.unique_sources}</div></div>
  <div><div class="k">Live API</div><div class="v" style="color:${CLS_COLOR.api}">${data.totals.api}</div></div>
  <div><div class="k">Curated</div><div class="v" style="color:${CLS_COLOR.source}">${data.totals.source}</div></div>
  <div><div class="k">Canonical</div><div class="v" style="color:${CLS_COLOR.canon}">${data.totals.canon}</div></div>
  <div><div class="k">Unverified</div><div class="v" style="color:${CLS_COLOR.unverified}">${data.totals.unverified}</div></div>
</div>
<table>
<thead><tr><td class="n">#</td><td class="src">Source</td><td class="cls">Class</td><td class="cnt">Cites</td><td class="secs">Sections</td></tr></thead>
<tbody>${rows}</tbody>
</table>
<footer>Sovereign IQ · classification logic in citation_verifier.py · unverified entries should be manually reviewed before external publication</footer>
</body></html>`;
}

function escapeHTML(s) {
  return String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
